import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  type OrdemServico,
  type StatusRelatorio,
  type ResumoRelatorios,
} from "../api";
import ErrorState from "./ErrorState";
import { IconSearch, IconRefresh } from "../icons";

type Aba = "resumo" | "pendentes" | "enviados" | "busca";

const ABAS: { key: Aba; label: string }[] = [
  { key: "resumo", label: "Resumo" },
  { key: "pendentes", label: "Pendentes" },
  { key: "enviados", label: "Enviados" },
  { key: "busca", label: "Buscar OS / cliente" },
];

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/** Como está o prazo desta OS pendente — vira o "pill" colorido da tabela. */
function situacaoPrazo(r: StatusRelatorio): { texto: string; tom: string } {
  if (r.enviado) return { texto: "Enviado", tom: "ok" };
  const d = r.diasRestantes;
  if (d == null) return { texto: "Pendente", tom: "warn" };
  if (d < 0) return { texto: `Atrasado ${Math.abs(d)}d`, tom: "danger" };
  if (d === 0) return { texto: "Vence hoje", tom: "danger" };
  if (d <= 2) return { texto: `Faltam ${d}d`, tom: "warn" };
  return { texto: `Faltam ${d}d`, tom: "ok" };
}

/**
 * Painel administrativo de relatórios dentro da Gestão da Operação.
 *
 * Os dados vêm do sistema de Relatórios (dono das OS e dos documentos
 * entregues) por HTTP, via proxy do backend — nada é gravado aqui. Mostra o
 * resumo, o que ainda não foi entregue, o que já foi, e uma busca por número
 * de OS ou cliente.
 */
export default function PainelRelatoriosView() {
  const [aba, setAba] = useState<Aba>("resumo");
  const [itens, setItens] = useState<StatusRelatorio[]>([]);
  const [resumo, setResumo] = useState<ResumoRelatorios | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Busca de OS (aba própria) — é independente do painel de status.
  const [busca, setBusca] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [buscaErro, setBuscaErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<OrdemServico[] | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.relatorios.status();
      setItens(r.itens ?? []);
      setResumo(r.resumo ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const pendentes = useMemo(() => itens.filter((i) => !i.enviado), [itens]);
  const enviados = useMemo(() => itens.filter((i) => i.enviado), [itens]);

  // Pendentes primeiro os mais críticos (atrasado → vence antes).
  const pendentesOrdenados = useMemo(
    () => [...pendentes].sort((a, b) => (a.diasRestantes ?? 0) - (b.diasRestantes ?? 0)),
    [pendentes],
  );
  const enviadosOrdenados = useMemo(
    () => [...enviados].sort((a, b) => String(b.enviadoEm ?? "").localeCompare(String(a.enviadoEm ?? ""))),
    [enviados],
  );

  async function pesquisar(e?: React.FormEvent) {
    e?.preventDefault();
    const q = busca.trim();
    setBuscando(true);
    setBuscaErro(null);
    try {
      const r = await api.relatorios.pesquisarOs(q);
      setResultado(r.itens ?? []);
    } catch (err) {
      setResultado(null);
      setBuscaErro(err instanceof ApiError ? err.message : "Falha ao pesquisar");
    } finally {
      setBuscando(false);
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={carregar} />;

  const tabela = (linhas: StatusRelatorio[], modo: "pendentes" | "enviados") => (
    <table className="table table-compact">
      <thead>
        <tr>
          <th>OS</th>
          <th>Cliente</th>
          <th>Equipamento</th>
          <th>Fim do serviço</th>
          <th>{modo === "pendentes" ? "Prazo de envio" : "Enviado em"}</th>
          <th>Situação</th>
        </tr>
      </thead>
      <tbody>
        {linhas.map((r) => {
          const s = situacaoPrazo(r);
          return (
            <tr key={r.id}>
              <td>
                <strong>{r.numero}</strong>
              </td>
              <td>{r.cliente}</td>
              <td>
                {r.equipamento || "—"}
                {r.temAnalise && <span className="badge badge-soft">com análise</span>}
              </td>
              <td>{dataBR(r.fimServico)}</td>
              <td>{modo === "pendentes" ? dataBR(r.prazoEnvio) : dataBR(r.enviadoEm)}</td>
              <td>
                <span className={`pill pill-${s.tom}`}>{s.texto}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="almox-tabs">
          {ABAS.map((a) => (
            <button
              key={a.key}
              className={`almox-tab ${aba === a.key ? "active" : ""}`}
              onClick={() => setAba(a.key)}
            >
              {a.label}
              {a.key === "pendentes" && pendentes.length > 0 ? ` (${pendentes.length})` : ""}
            </button>
          ))}
        </div>

        <div className="toolbar-right">
          <button className="btn-secondary" onClick={carregar} title="Atualizar">
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty">Carregando…</div>
      ) : aba === "resumo" ? (
        <div className="painel-rel">
          <div className="cards">
            <div className="card">
              <span className="card-label">OS concluídas</span>
              <strong className="card-valor">{resumo?.total ?? 0}</strong>
            </div>
            <div className="card">
              <span className="card-label">Relatórios enviados</span>
              <strong className="card-valor tom-ok">{resumo?.enviados ?? 0}</strong>
            </div>
            <div className="card">
              <span className="card-label">Pendentes</span>
              <strong className="card-valor tom-warn">{resumo?.pendentes ?? 0}</strong>
            </div>
            <div className="card">
              <span className="card-label">Atrasados</span>
              <strong className="card-valor tom-danger">{resumo?.atrasados ?? 0}</strong>
            </div>
            <div className="card">
              <span className="card-label">Vencem hoje</span>
              <strong className="card-valor tom-danger">{resumo?.venceHoje ?? 0}</strong>
            </div>
          </div>

          <p className="hint">
            O prazo de envio do relatório final conta do fim do serviço: 15 dias para OS com
            análise de óleo e 3 dias para as demais. "Enviado" é a OS que já tem o relatório
            mesclado gerado no sistema de Relatórios.
          </p>

          {pendentesOrdenados.length > 0 && (
            <>
              <h4 className="painel-rel-titulo">Mais urgentes</h4>
              {tabela(pendentesOrdenados.slice(0, 5), "pendentes")}
            </>
          )}
        </div>
      ) : aba === "pendentes" ? (
        <div className="table-wrap">
          {pendentesOrdenados.length === 0 ? (
            <div className="empty">Nenhum relatório pendente. Tudo entregue.</div>
          ) : (
            tabela(pendentesOrdenados, "pendentes")
          )}
        </div>
      ) : aba === "enviados" ? (
        <div className="table-wrap">
          {enviadosOrdenados.length === 0 ? (
            <div className="empty">Nenhum relatório enviado ainda.</div>
          ) : (
            tabela(enviadosOrdenados, "enviados")
          )}
        </div>
      ) : (
        <div className="table-wrap">
          <form className="crud-toolbar" onSubmit={pesquisar}>
            <div className="search-wrap">
              <IconSearch size={17} />
              <input
                className="search"
                placeholder="Número da OS ou nome do cliente…"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <button className="btn-primary" type="submit" disabled={buscando}>
              {buscando ? "Buscando…" : "Buscar"}
            </button>
          </form>

          {buscaErro && <div className="form-error">{buscaErro}</div>}

          {resultado == null ? (
            <div className="empty">
              Busque por número de OS ou cliente. Deixe em branco e clique em Buscar para ver as
              mais recentes.
            </div>
          ) : resultado.length === 0 ? (
            <div className="empty">Nenhuma OS encontrada para "{busca}".</div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>OS</th>
                  <th>Cliente</th>
                  <th>Equipamento</th>
                  <th>Tipo de serviço</th>
                  <th>Local</th>
                  <th>Período</th>
                  <th>Responsável</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {resultado.map((os) => (
                  <tr key={os.id}>
                    <td>
                      <strong>{os.numero}</strong>
                    </td>
                    <td>{os.cliente}</td>
                    <td>{os.equipamento || "—"}</td>
                    <td>{os.tipoServico || "—"}</td>
                    <td>{os.local || "—"}</td>
                    <td>
                      {dataBR(os.inicio)} → {dataBR(os.fim)}
                    </td>
                    <td>{os.responsavel || "—"}</td>
                    <td>
                      <span className={`pill pill-${os.status === "concluida" ? "ok" : "warn"}`}>
                        {os.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
